import React from 'react';

import dayjs from 'dayjs';

import { Box, Typography, alpha, useTheme } from '@mui/material';

import { CustomRule } from '~/types/loyaltyContract';

interface RowDetailPanelProps {
  row: CustomRule;
  columnVisibilityModel: Record<string, boolean>;
}

const RowDetailPanel: React.FC<RowDetailPanelProps> = ({ row, columnVisibilityModel }) => {
  const theme = useTheme();

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value instanceof Date) return dayjs(value).format('DD/MM/YYYY HH:mm');
    if (typeof value === 'string' && value.length === 0) return '—';
    return String(value);
  };

  const allFields = [
    { fieldName: 'pointsValue', label: 'Points Value', value: row.pointsValue, priority: 1 },
    { fieldName: 'pointsMultiplier', label: 'Points Multiplier', value: row.pointsMultiplier, priority: 1 },
    { fieldName: 'pointsRatio', label: 'Points Ratio', value: row.pointsRatio, priority: 1 },
    { fieldName: 'pointsCap', label: 'Points Cap', value: row.pointsCap, priority: 1 },
    { fieldName: 'region', label: 'Region', value: row.region, priority: 1 },
    { fieldName: 'earnKey', label: 'Earn Key', value: row.earnKey, priority: 1 },
    { fieldName: 'promoKey', label: 'Promo Key', value: row.promoKey, priority: 1 },
    {
      fieldName: 'startDate',
      label: 'Start Date',
      value: row.startDate ? dayjs(row.startDate).format('DD/MM/YYYY') : '—',
      priority: 1,
    },
    {
      fieldName: 'endDate',
      label: 'End Date',
      value: row.endDate ? dayjs(row.endDate).subtract(1, 'day').format('DD/MM/YYYY') : '—',
      priority: 1,
    },

    { fieldName: 'id', label: 'ID', value: row.id, priority: 2 },
    { fieldName: 'name', label: 'Name', value: row.name, priority: 2 },
    { fieldName: 'type', label: 'Type', value: row.type, priority: 2 },
    { fieldName: 'purpose', label: 'Purpose', value: row.purpose, priority: 2 },
    { fieldName: 'pointsType', label: 'Points Type', value: row.pointsType, priority: 2 },
    { fieldName: 'productType', label: 'Product Type', value: row.productType, priority: 2 },
    { fieldName: 'offerId', label: 'Offer ID', value: row.offerId, priority: 2 },
    { fieldName: 'packageId', label: 'Package ID', value: row.packageId, priority: 2 },
  ];

  const fields = allFields
    .filter((field) => {
      const isVisible = columnVisibilityModel[field.fieldName] !== false;
      return isVisible;
    })
    .sort((a, b) => a.priority - b.priority);

  return (
    <Box
      sx={{
        p: 0.5,
        bgcolor: alpha(theme.palette.background.paper, 0.5),
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        width: '100%',
        maxWidth: {
          xs: 'calc(100vw - 55px)',
          sm: 'calc(100vw - 85px)',
          md: 'calc(100vw - 250px)',
          lg: 'calc(100vw - 280px)',
          xl: '1400px',
        },
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          },
          gap: 2,
          width: '100%',
        }}
      >
        {fields.map((field, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              p: 1.5,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.background.paper, 0.8),
              border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
              transition: 'all 0.2s ease',
              minWidth: 0,
              overflow: 'hidden',
              '&:hover': {
                bgcolor: alpha(theme.palette.background.paper, 0.95),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: theme.typography.fontWeightBold,
                color: 'text.secondary',
                textTransform: 'uppercase',
                fontSize: theme.typography.caption.fontSize,
                letterSpacing: '0.5px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {field.label}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                fontWeight: theme.typography.fontWeightMedium,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                hyphens: 'auto',
              }}
            >
              {formatValue(field.value)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default RowDetailPanel;
